import {
  isJust,
  isNothing,
  just,
  nothing,
  type Maybe,
  matchMaybe,
  toMaybe,
} from "./maybe";
import type { EntityLink } from "./entityFrontmatter";
import type { DatabaseEntrySummary } from "./tauri/database";
import type { ZwDatabaseRelationPair } from "./zwConfig";

/** One side of a relationship pair (builtin or project custom). */
export type RelationSide = {
  id: string;
  label: string;
};

/** A ↔ B vocabulary entry. Symmetric when `a.id === b.id`. */
export type RelationPairDef = {
  a: RelationSide;
  b: RelationSide;
};

/** Built-in relationship pairs (#72). Each side is selectable as the owned label. */
export const BUILTIN_RELATION_PAIRS: readonly RelationPairDef[] = [
  {
    a: { id: "related_to", label: "Related to" },
    b: { id: "related_to", label: "Related to" },
  },
  {
    a: { id: "ally_of", label: "Ally of" },
    b: { id: "ally_of", label: "Ally of" },
  },
  {
    a: { id: "rival_of", label: "Rival of" },
    b: { id: "rival_of", label: "Rival of" },
  },
  {
    a: { id: "child_of", label: "Child of" },
    b: { id: "parent_of", label: "Parent of" },
  },
  {
    a: { id: "member_of", label: "Member of" },
    b: { id: "has_member", label: "Has member" },
  },
  {
    a: { id: "leads", label: "Leads" },
    b: { id: "led_by", label: "Led by" },
  },
  {
    a: { id: "located_in", label: "Located in" },
    b: { id: "contains", label: "Contains" },
  },
  {
    a: { id: "part_of", label: "Part of" },
    b: { id: "has_part", label: "Has part" },
  },
  {
    a: { id: "created_by", label: "Created by" },
    b: { id: "creator_of", label: "Creator of" },
  },
] as const;

/** Flat selectable sides derived from builtin pairs (unique by id). */
export const BUILTIN_RELATIONS: readonly RelationSide[] = (() => {
  const byId = new Map<string, RelationSide>();
  for (const pair of BUILTIN_RELATION_PAIRS) {
    byId.set(pair.a.id, pair.a);
    byId.set(pair.b.id, pair.b);
  }
  return [...byId.values()];
})();

export type BuiltinRelationId = string;

export const CUSTOM_RELATION_OPTION = "__custom__";

export function newEntityLinkId(): string {
  return `lnk_${Math.random().toString(36).slice(2, 8)}`;
}

/** Slugify a free-text relation label into a `custom:…` id. */
export function customRelationId(draft: string): Maybe<string> {
  const slug = draft
    .trim()
    .toLowerCase()
    .replace(/\s+/g, "_")
    .replace(/[^a-z0-9_]/g, "");
  return slug.length > 0 ? just(`custom:${slug}`) : nothing;
}

/** Build a project custom pair from dialog labels. Other side blank = symmetric. */
export function buildCustomRelationPair(
  thisLabel: string,
  otherLabel: Maybe<string>,
  newPairId: () => string,
): Maybe<ZwDatabaseRelationPair> {
  const aLabel = thisLabel.trim();
  const aIdM = customRelationId(aLabel);
  if (isNothing(aIdM)) return nothing;
  const aId = aIdM.value;
  const bRaw = matchMaybe(otherLabel, {
    nothing: () => "",
    just: (label) => label.trim(),
  });
  if (bRaw.length === 0 || bRaw.toLowerCase() === aLabel.toLowerCase()) {
    return just({
      id: newPairId(),
      aId,
      aLabel,
      bId: aId,
      bLabel: aLabel,
    });
  }
  const bIdM = customRelationId(bRaw);
  if (isNothing(bIdM)) return nothing;
  return just({
    id: newPairId(),
    aId,
    aLabel,
    bId: bIdM.value,
    bLabel: bRaw,
  });
}

/** Convert a project config pair into the shared RelationPairDef shape. */
export function relationPairFromConfig(
  pair: ZwDatabaseRelationPair,
): RelationPairDef {
  return {
    a: { id: pair.aId, label: pair.aLabel },
    b: { id: pair.bId, label: pair.bLabel },
  };
}

/** Builtin pairs plus project custom pairs. */
export function mergedRelationPairs(
  customPairs: Maybe<readonly ZwDatabaseRelationPair[]>,
): RelationPairDef[] {
  const custom = matchMaybe(customPairs, {
    nothing: () => [] as RelationPairDef[],
    just: (pairs) => pairs.map(relationPairFromConfig),
  });
  return [...BUILTIN_RELATION_PAIRS, ...custom];
}

/** All selectable sides (unique by id) from merged pairs. */
export function relationSidesFromPairs(
  pairs: RelationPairDef[],
): RelationSide[] {
  const byId = new Map<string, RelationSide>();
  for (const pair of pairs) {
    byId.set(pair.a.id, pair.a);
    byId.set(pair.b.id, pair.b);
  }
  return [...byId.values()].sort((a, b) =>
    a.label.localeCompare(b.label, void 0, { sensitivity: "base" }),
  );
}

function inverseMapFromPairs(pairs: RelationPairDef[]): Map<string, string> {
  const map = new Map<string, string>();
  for (const pair of pairs) {
    map.set(pair.a.id, pair.b.id);
    map.set(pair.b.id, pair.a.id);
  }
  return map;
}

function labelMapFromPairs(pairs: RelationPairDef[]): Map<string, string> {
  const map = new Map<string, string>();
  for (const pair of pairs) {
    map.set(pair.a.id, pair.a.label);
    map.set(pair.b.id, pair.b.label);
  }
  return map;
}

/** Inverse relation id for display on the non-owning entry. */
export function inverseRelation(
  relation: string,
  customPairs: Maybe<readonly ZwDatabaseRelationPair[]> = nothing,
): string {
  const map = inverseMapFromPairs(mergedRelationPairs(customPairs));
  return map.get(relation) ?? relation;
}

/** Human label for a stored relation id. */
export function relationLabel(
  relation: string,
  customPairs: Maybe<readonly ZwDatabaseRelationPair[]> = nothing,
): string {
  const labels = labelMapFromPairs(mergedRelationPairs(customPairs));
  const knownM = toMaybe(labels.get(relation));
  if (isJust(knownM) && knownM.value.length > 0) return knownM.value;
  if (relation.startsWith("custom:")) {
    const slug = relation.slice("custom:".length);
    return slug.replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase());
  }
  return relation;
}

export type IncomingEntityLink = {
  sourceFile: string;
  link: EntityLink;
};

/** A relationship row relative to the active entry. */
export type EntityRelationshipRow = {
  key: string;
  sourceFile: string;
  otherFile: string;
  direction: "out" | "in";
  link: EntityLink;
  /** Relation id to show for this viewer (owned or inverse). */
  displayRelation: string;
};

/** Scans every worldbuilding entry's `links[]` for edges pointing at
 * `targetFile`, the inverse of the active entry's own outbound list. */
export function findIncomingLinks(
  targetFile: string,
  entries: DatabaseEntrySummary[],
): IncomingEntityLink[] {
  const incoming: IncomingEntityLink[] = [];
  for (const entry of entries) {
    if (entry.isMap || entry.name === targetFile) continue;
    for (const link of entry.fields.links) {
      if (link.file === targetFile) {
        incoming.push({ sourceFile: entry.name, link });
      }
    }
  }
  return incoming.sort((a, b) =>
    a.sourceFile.localeCompare(b.sourceFile, void 0, {
      sensitivity: "base",
    }),
  );
}

/** Merges outbound + inbound edges. Inbound rows use the dictionary inverse
 * for `displayRelation` so each page shows the correct prefix label. */
export function listEntityRelationships(
  activeFile: string,
  outgoing: EntityLink[],
  entries: DatabaseEntrySummary[],
  customPairs: Maybe<readonly ZwDatabaseRelationPair[]> = nothing,
): EntityRelationshipRow[] {
  // Prefer the live outgoing list for the active entry so inbound scans on
  // other pages see freshly saved links after outline refresh, and the active
  // page never reads a stale outline copy of itself.
  const entriesForScan = entries.map((entry) => {
    if (entry.name !== activeFile || entry.isMap) return entry;
    return {
      ...entry,
      fields: { ...entry.fields, links: outgoing },
    };
  });

  const rows: EntityRelationshipRow[] = outgoing.map((link) => ({
    key: `out:${link.id}`,
    sourceFile: activeFile,
    otherFile: link.file,
    direction: "out" as const,
    link,
    displayRelation: link.relation,
  }));

  for (const row of findIncomingLinks(activeFile, entriesForScan)) {
    // Skip if we already have this edge as outbound (same id) — shouldn't
    // happen for a single-owned link, but guards against duplicates.
    if (rows.some((existing) => existing.link.id === row.link.id)) continue;
    rows.push({
      key: `in:${row.sourceFile}:${row.link.id}`,
      sourceFile: row.sourceFile,
      otherFile: row.sourceFile,
      direction: "in",
      link: row.link,
      displayRelation: inverseRelation(row.link.relation, customPairs),
    });
  }

  return rows.sort((a, b) =>
    a.otherFile.localeCompare(b.otherFile, void 0, {
      sensitivity: "base",
    }),
  );
}
